import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { createColumnHelper } from "@tanstack/react-table";
import { Tooltip } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserAlt } from "@fortawesome/free-solid-svg-icons";
import { useGroups } from "../../../../Settings/General/OrganisationLevelGroups/context";
import { useEarningGroupsContext } from "../../../earningGroupsContext";
import { useVisible } from "../../../../../utils/hooks/useVisible";
import { useAccess } from "../../../../../utils/hooks/access";
import { useOrgSettingsResourceLabel } from "../../../../../utils/hooks/useOrgSettingsResourceLabel";
import { getCellValue } from "../../../Team/Table/useColumns/useEarningGroupsColumns";
import { getPrimaryActiveResource, renderUserName } from "../../../../../utils/helpers/user";
import { renderSettingsResourceLabel } from "../../../../../utils/helpers/settings";
import { currencyFormatter } from "../../../../../utils/helpers/currencyFormatter";

const columnHelper = createColumnHelper();

const getUniquePrimaryResources = (main, others, key) => {
    const mainItem = getPrimaryActiveResource(main);

    const otherItems = others?.map((p) => getPrimaryActiveResource(p || [])) || [];

    return [mainItem, ...otherItems]
        .filter(Boolean)
        .reduce((acc, curr) => {
            if (!acc.find(item => item[key] === curr[key])) {
                acc.push(curr);
            }
            return acc;
        }, []);
};

const CellwithTooltip = ({ id: _id, key, value }) => {
    const textRef = useRef(null);
    const id = key + _id;
    const [isTruncated, setIsTruncated] = useState(false);
    const { visible, toggle } = useVisible();

    useEffect(() => {
        if (!textRef.current) return;
        const el = textRef.current;
        const check = () => {
            setIsTruncated(el.scrollWidth > el.clientWidth);
        };
        check(); // initial
        const observer = new ResizeObserver(check);
        observer.observe(el);
        return () => observer.disconnect();
    }, [value]);

    return (
        <>
            <span
                ref={textRef}
                id={id}
                className="text-truncate rounded px-2 py-1"
                style={{
                    border: "1px dashed #525f7f",
                    color: "#525f7f"
                }}
            >
                {value}
            </span>

            {isTruncated && (
                <Tooltip
                    target={id}
                    isOpen={visible}
                    placement="auto-left"
                    toggle={toggle}
                >
                    <span className="text-sm font-weight-bolder px-2 m-2">
                        {value}
                    </span>
                </Tooltip>
            )}
        </>
    )
}

export const useColumns = ({ viewProfile }) => {
    const { t } = useTranslation();
    const { groups } = useGroups();
    const renderOrgLevelResourceLabel = useOrgSettingsResourceLabel();
    const { data: earningGroups } = useEarningGroupsContext();
    const { hasAccess: canViewRates } = useAccess("canViewRates");

    return useMemo(() => {
        const columns = [
            columnHelper.accessor("name", {
                header: t("name"),
                cell: ({ row: { original } }) => {
                    return (
                        <div className="text-truncate">
                            {viewProfile && (
                                <FontAwesomeIcon
                                    icon={faUserAlt}
                                    className="text-primary mr-2 cursor-pointer"
                                    onClick={() => viewProfile(original)}
                                />
                            )}
                            <span className="font-weight-bold text-primary">
                                {original?.name || renderUserName(original, false)}
                            </span>
                        </div>
                    )
                }
            }),
            columnHelper.accessor("employeeNumbers", {
                header: t("employee-numbers"),
                cell: ({ row: { original } }) => {
                    return (
                        <div className="d-flex flex-wrap gap-1">
                            {original?.employeeNumbers?.map((empNumber) => (
                                <CellwithTooltip
                                    id={empNumber + original.id}
                                    key={"employeeNumbers" + empNumber + original.id}
                                    value={empNumber}
                                />
                            ))}
                        </div>
                    );
                }
            }),
            columnHelper.accessor("companies", {
                header: t("companies"),
                cell: ({ row: { original } }) => {
                    const companies = original?.companies;
                    return (
                        <div className="d-flex flex-wrap gap-1">
                            {companies?.map((company) => (
                                <CellwithTooltip
                                    id={company.id + original.id}
                                    key={"companies" + company.id + original.id}
                                    value={company.name}
                                />
                            ))}
                        </div>
                    )
                }
            }),
            columnHelper.accessor("payGroups", {
                header: t("pay-groups"),
                cell: ({ row: { original } }) => {
                    const payGroups = original?.payGroups;
                    return (
                        <div className="d-flex flex-wrap gap-1">
                            {payGroups?.map((payGroup) => (
                                <CellwithTooltip
                                    id={payGroup.id + original.id}
                                    key={"payGroups" + payGroup.id + original.id}
                                    value={renderSettingsResourceLabel(payGroup)}
                                />
                            ))}
                        </div>
                    )
                }
            }),
            columnHelper.accessor("calculationGroups", {
                header: t("calculation-groups"),
                cell: ({ row: { original } }) => {
                    const calculationGroups = original?.calculationGroups;
                    return (
                        <div className="d-flex flex-wrap gap-1">
                            {calculationGroups?.map((calcGroup) => (
                                <CellwithTooltip
                                    id={calcGroup.id + original.id}
                                    key={"calculationGroups" + calcGroup.id + original.id}
                                    value={renderSettingsResourceLabel(calcGroup)}
                                />
                            ))}
                        </div>
                    )
                }
            }),
            columnHelper.accessor("locations", {
                header: t("location"),
                cell: ({ row: { original } }) => {
                    const locations = getUniquePrimaryResources(
                        original.locations,
                        original.otherProfiles?.map((p) => p.locations),
                        "locationId"
                    );

                    return locations.length ? (
                        <div className="d-flex flex-wrap gap-1">
                            {locations.map((location) => (
                                <CellwithTooltip
                                    key={"location" + location.locationId + original.id}
                                    id={location.locationId + original.id}
                                    value={renderSettingsResourceLabel(location)}
                                />
                            ))}
                        </div>
                    ) : null;
                }
            }),
            columnHelper.accessor("allJobs", {
                header: t("job"),
                cell: ({ row: { original } }) => {
                    const jobs = getUniquePrimaryResources(
                        original.jobs,
                        original.otherProfiles?.map(p => p.jobs),
                        "jobId"
                    );

                    return jobs.length ? (
                        <div className="d-flex flex-wrap gap-1">
                            {jobs.map((job) => (
                                <CellwithTooltip
                                    key={"job" + job.jobId + original.id}
                                    id={job.jobId + original.id}
                                    value={renderSettingsResourceLabel(job)}
                                />
                            ))}
                        </div>
                    ) : null;
                }
            }),
        ];
        if (groups?.length > 0) {
            const groupColumns = groups.map((group) =>
                columnHelper.accessor(group.level, {
                    header: group.description,
                    cell: ({ row: { original } }) => {
                        const levels = original?.[group.level];
                        return (
                            <div className="d-flex flex-wrap gap-1">
                                {levels?.map((level) => (
                                    <CellwithTooltip
                                        id={level.id + original.id}
                                        key={group.level + level.id + original.id}
                                        value={renderOrgLevelResourceLabel(level)}
                                    />
                                ))}
                            </div>
                        )
                    }
                }),
            );
            columns.push(...groupColumns);
        }

        if (earningGroups?.length > 0) {
            const earningGroupColumns = earningGroups
                .filter((item) => {
                    if (item.display === "total") {
                        return canViewRates;
                    }
                    return true;
                })
                ?.map((item) => columnHelper.accessor(item.id, {
                    header: item.code,
                    size: 60,
                    cell: ({ row: { original } }) => {
                        return getCellValue(item, original?.overall)
                    }
                }));
            columns.push(...earningGroupColumns);
        }

        if (canViewRates) {
            columns.push(columnHelper.accessor("payment", {
                header: t("payment"),
                cell: ({ row: { original } }) => {
                    const job = getPrimaryActiveResource(original.jobs);
                    return currencyFormatter(original.payment || 0, 2, job?.currency);
                }
            }))
        }
        return columns;
    }, [canViewRates, earningGroups, groups, t, viewProfile, renderOrgLevelResourceLabel]);
}